import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Globe, 
  Loader2,
  ExternalLink,
  Check,
  Search,
  MoreVertical,
  Power
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton, SkeletonTable } from "../components/ui/skeleton";
import CompetitorFormDialog from "../components/competitors/CompetitorFormDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCompetitor = {
  name: "", website_url: "", linkedin_url: "", instagram_url: "",
  twitter_url: "", youtube_url: "", facebook_url: "", tiktok_url: "", description: "",
};

const socialPlatforms = [
  { key: "linkedin_url", label: "LinkedIn" },
  { key: "instagram_url", label: "Instagram" },
  { key: "twitter_url", label: "Twitter/X" },
  { key: "youtube_url", label: "YouTube" },
  { key: "facebook_url", label: "Facebook" },
  { key: "tiktok_url", label: "TikTok" },
];

export default function Competitors() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [formData, setFormData] = useState(emptyCompetitor);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchCompetitors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/competitors?region=${region}&brand=${brand}`);
      setCompetitors(res.data);
    } catch (error) {
      toast.error("Failed to load competitors");
    } finally {
      setLoading(false);
    }
  }, [region, brand]);

  useEffect(() => { setLoading(true); fetchCompetitors(); }, [fetchCompetitors, region, brand]);

  const filteredCompetitors = competitors.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCreateDialog = () => { setSelectedCompetitor(null); setFormData(emptyCompetitor); setDialogOpen(true); };
  const openEditDialog = (comp) => { setSelectedCompetitor(comp); setFormData({...emptyCompetitor, ...comp}); setDialogOpen(true); };
  const openDeleteDialog = (comp) => { setSelectedCompetitor(comp); setDeleteDialogOpen(true); };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (selectedCompetitor) {
        await axios.put(`${API}/competitors/${selectedCompetitor.id}`, formData);
        toast.success("Competitor updated");
      } else {
        await axios.post(`${API}/competitors`, { ...formData, region, brand });
        toast.success("Competitor added");
      }
      setDialogOpen(false);
      fetchCompetitors();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/competitors/${selectedCompetitor.id}`);
      toast.success("Competitor deleted");
      setDeleteDialogOpen(false);
      fetchCompetitors();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const toggleActive = async (comp) => {
    try {
      await axios.put(`${API}/competitors/${comp.id}`, { is_active: !comp.is_active });
      fetchCompetitors();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto space-y-6" data-testid="competitors-loading">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between animate-fadeIn">
          <div>
            <Skeleton className={`h-8 w-40 mb-2 ${darkMode ? 'bg-[#21262d]' : ''}`} />
            <Skeleton className={`h-4 w-48 ${darkMode ? 'bg-[#21262d]' : ''}`} />
          </div>
          <Skeleton className={`h-10 w-36 rounded-lg ${darkMode ? 'bg-[#21262d]' : ''}`} />
        </div>
        {/* Search Skeleton */}
        <Skeleton className={`h-11 w-80 rounded-lg animate-fadeIn stagger-1 ${darkMode ? 'bg-[#21262d]' : ''}`} />
        {/* Table Skeleton */}
        <div className="animate-fadeIn stagger-2">
          <SkeletonTable rows={8} cols={5} darkMode={darkMode} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="competitors-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Competitors
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {competitors.length} of 100 competitors tracked
          </p>
        </div>
        <Button onClick={openCreateDialog} disabled={competitors.length >= 100} className="bg-[#DC143C] hover:bg-[#b91031] text-white" data-testid="add-competitor-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Competitor
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        <input
          placeholder="Search competitors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={`w-full pl-10 pr-4 py-2.5 text-sm rounded-lg ${
            darkMode 
              ? 'bg-[#21262d] border border-[#30363d] text-white placeholder:text-gray-500' 
              : 'bg-gray-50 border-0 text-gray-900 placeholder:text-gray-400'
          }`}
          data-testid="search-input"
        />
      </div>

      {/* Competitors Table */}
      <div className={`rounded-xl overflow-hidden ${darkMode ? 'bg-[#161b22] border border-[#30363d]' : 'bg-white border border-gray-100 shadow-sm'}`}>
        {filteredCompetitors.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className={darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Company</th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Website</th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Social</th>
                <th className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                <th className={`px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#21262d]">
              {filteredCompetitors.map((comp, index) => (
                <tr key={comp.id} className={`${!comp.is_active && 'opacity-50'} ${darkMode ? 'hover:bg-[#21262d]' : 'hover:bg-gray-50'}`} data-testid={`competitor-row-${index}`}>
                  <td className="px-5 py-4">
                    <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{comp.name}</div>
                    {comp.description && <div className={`text-sm truncate max-w-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{comp.description}</div>}
                  </td>
                  <td className="px-5 py-4">
                    {comp.website_url ? (
                      <a href={comp.website_url} target="_blank" rel="noopener noreferrer" className="text-[#DC143C] hover:underline text-sm flex items-center gap-1">
                        {new URL(comp.website_url).hostname}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>—</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {socialPlatforms.filter(p => comp[p.key]).length} linked
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <Badge className={comp.is_active ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-gray-100 text-gray-500 border-0'}>
                      {comp.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className={darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
                        <DropdownMenuItem onClick={() => openEditDialog(comp)} className={darkMode ? 'text-gray-300 focus:bg-[#21262d]' : ''}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(comp)} className={darkMode ? 'text-gray-300 focus:bg-[#21262d]' : ''}>
                          <Power className="w-4 h-4 mr-2" /> {comp.is_active ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openDeleteDialog(comp)} className="text-red-500 focus:text-red-500">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className={`p-16 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Globe className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">No competitors yet</p>
            <p className="text-sm mt-1">Add your first competitor to start tracking</p>
            <Button onClick={openCreateDialog} className="mt-4 bg-[#DC143C] hover:bg-[#b91031] text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Competitor
            </Button>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <CompetitorFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        darkMode={darkMode}
        selectedCompetitor={selectedCompetitor}
        formData={formData}
        setFormData={setFormData}
        saving={saving}
        handleSave={handleSave}
        socialPlatforms={socialPlatforms}
      />

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={darkMode ? 'text-white' : ''}>Delete Competitor</AlertDialogTitle>
            <AlertDialogDescription className={darkMode ? 'text-gray-400' : ''}>
              Are you sure you want to delete {selectedCompetitor?.name}?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white hover:bg-[#30363d]' : ''}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
